import type { ImportProbeDTO } from '@hearth/shared';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, FolderOpen, FolderSearch, ScanSearch, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Page } from '../../../components/layout/Page';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Button, Card, CardBody, CardHeader, Checkbox, EmptyState, Input, Spinner, TBody, TD, TH, THead, TR, Table, TableMessageRow, useToast } from '../../../components/ui';
import { useAuth } from '../../../hooks/useAuth';
import { useLookups } from '../../../hooks/useMeta';
import { errorMessage } from '../../../lib/api';
import { formatDate, formatFileSize, formatNumber, pluralize } from '../../../lib/format';
import { ImportOptionCards, type ImportOptions, validCountry } from '../ImportOptions';
import { type DirectoryLike, type ScanCandidate, type ScanProgress, type ScanResult, type WalkOptions, SCAN_LIMITS, findCopies, sha256, walkDirectory, walkFileList } from './folderScan';
import { type ImportStep, type ProbeOutcome, checkFiles, importBlocker, importFiles, newBatchId, rankCandidates, scanApi, worthImporting } from './scanImport';

type DirectoryPickerWindow = Window & { showDirectoryPicker?: (options: { mode: 'read'; id?: string }) => Promise<DirectoryLike> };

type Phase = 'idle' | 'walking' | 'ready' | 'checking' | 'importing';

/** Files that could not be uploaded, handed to the batch page (they have no import to show there). */
export type NotImported = { path: string; message: string }[];

/**
 * Folder scan: the browser lists a folder of this computer, Hearth checks the chosen files for leads without keeping
 * them, and the files worth it become one import each, grouped as a batch (/imports/batch/:batchId).
 */
export function ScanPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { meta } = useLookups();
  const folderInput = useRef<HTMLInputElement | null>(null);
  const abort = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  const [phase, setPhase] = useState<Phase>('idle');
  const [walkProgress, setWalkProgress] = useState<ScanProgress & { copies: boolean }>({ visited: 0, found: 0, copies: false });
  const [scan, setScan] = useState<ScanResult | null>(null);
  const [candidates, setCandidates] = useState<ScanCandidate[]>([]);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const [outcomes, setOutcomes] = useState<ReadonlyMap<string, ProbeOutcome>>(new Map());
  const [inFlight, setInFlight] = useState<ReadonlySet<string>>(new Set());
  const [steps, setSteps] = useState<ReadonlyMap<string, ImportStep>>(new Map());
  const [run, setRun] = useState<{ done: number; total: number } | null>(null);
  const [paused, setPaused] = useState(0);
  const [filter, setFilter] = useState('');
  const [options, setOptions] = useState<ImportOptions | null>(null);
  const hasPicker = typeof (window as DirectoryPickerWindow).showDirectoryPicker === 'function';

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      abort.current?.abort();
    };
  }, []);

  const begin = () => {
    abort.current?.abort();
    abort.current = new AbortController();
    return abort.current;
  };
  const busy = phase === 'walking' || phase === 'checking' || phase === 'importing';

  const scanFolder = async (walk: (walkOptions: WalkOptions) => Promise<ScanResult> | ScanResult) => {
    const controller = begin();
    setPhase('walking');
    setWalkProgress({ visited: 0, found: 0, copies: false });
    try {
      const result = await walk({ signal: controller.signal, onProgress: (progress) => setWalkProgress({ ...progress, copies: false }) });
      let list = rankCandidates(result.candidates, new Map());
      // Only in a secure context (https or localhost); elsewhere copies are simply not spotted.
      if (crypto.subtle && !controller.signal.aborted) {
        setWalkProgress({ visited: result.visited, found: list.length, copies: true });
        list = await findCopies(list, sha256, controller.signal);
      }
      setScan(result);
      setCandidates(list);
      setTicked(new Set(list.filter((candidate) => !candidate.tooLarge && !candidate.copyOf).map((candidate) => candidate.path)));
      setOutcomes(new Map());
      setSteps(new Map());
      setFilter('');
      setOptions(
        (current) =>
          current ?? {
            defaultOwnerId: user?.role === 'sales_rep' ? user.id : null,
            defaultSourceId: null,
            defaultTagIds: [],
            defaultCountry: meta?.settings.defaultCountry ?? 'CA',
            duplicatePolicy: 'review',
            defaultConsentBasis: 'none',
            businessKind: 'pizzeria',
          },
      );
      setPhase('ready');
    } catch (error) {
      // The folder itself could not be read: whatever was listed before stays.
      toast.error(errorMessage(error));
      setPhase(scan ? 'ready' : 'idle');
    }
  };

  const chooseFolder = async () => {
    const win = window as DirectoryPickerWindow;
    if (!win.showDirectoryPicker) {
      folderInput.current?.click();
      return;
    }
    let root: DirectoryLike;
    try {
      root = await win.showDirectoryPicker({ mode: 'read', id: 'hearth-lead-scan' });
    } catch (error) {
      // Closing the dialog is not an error.
      if (!(error instanceof DOMException && error.name === 'AbortError')) toast.error(errorMessage(error));
      return;
    }
    await scanFolder((walkOptions) => walkDirectory(root, walkOptions));
  };

  const blockers = new Map(candidates.map((candidate) => [candidate.path, importBlocker(candidate, outcomes.get(candidate.path))]));
  const importable = candidates.filter((candidate) => ticked.has(candidate.path) && !blockers.get(candidate.path));
  const checkable = importable.filter((candidate) => outcomes.get(candidate.path)?.kind !== 'read');

  const check = async () => {
    const list = checkable;
    const controller = begin();
    setPhase('checking');
    setRun({ done: 0, total: list.length });
    setPaused(0);
    const found = new Map<string, ProbeOutcome>();
    await checkFiles(list, scanApi.probe, {
      signal: controller.signal,
      onStart: (path) => setInFlight((current) => new Set(current).add(path)),
      onResult: (path, outcome) => {
        found.set(path, outcome);
        setOutcomes((current) => new Map(current).set(path, outcome));
        setInFlight((current) => new Set([...current].filter((value) => value !== path)));
        setRun((current) => current && { ...current, done: current.done + 1 });
      },
      onPause: (pausing) => setPaused((count) => count + (pausing ? 1 : -1)),
    });
    const all = new Map([...outcomes, ...found]);
    setCandidates((current) => rankCandidates(current, all));
    setTicked((current) => {
      const next = new Set(current);
      for (const [path, outcome] of found) {
        if (outcome.kind === 'failed') continue; // Not checked: stays ticked for another try.
        if (worthImporting(outcome)) next.add(path);
        else next.delete(path);
      }
      return next;
    });
    setInFlight(new Set());
    setPaused(0);
    setRun(null);
    setPhase('ready');
  };

  const startImport = async () => {
    if (!options) return;
    const list = importable;
    const controller = begin();
    const batchId = newBatchId();
    setPhase('importing');
    setSteps(new Map());
    setRun({ done: 0, total: list.length });
    const notImported: NotImported = [];
    const created = await importFiles(list, batchId, options, scanApi.upload, {
      signal: controller.signal,
      onStep: (path, step) => {
        setSteps((current) => new Map(current).set(path, step));
        if (step.state === 'uploading') return;
        if (step.state === 'failed') notImported.push({ path, message: step.message });
        setRun((current) => current && { ...current, done: current.done + 1 });
      },
    });
    void queryClient.invalidateQueries({ queryKey: ['imports', 'list'] });
    if (!mounted.current) return;
    if (created > 0) {
      navigate(`/imports/batch/${batchId}`, { state: { notImported } });
      return;
    }
    setRun(null);
    setPhase('ready');
    if (notImported.length > 0) toast.error('None of the files could be uploaded. The reason is next to each one.');
  };

  const toggle = (paths: string[], on: boolean) =>
    setTicked((current) => {
      const next = new Set(current);
      for (const path of paths) {
        if (on) next.add(path);
        else next.delete(path);
      }
      return next;
    });

  const needle = filter.trim().toLowerCase();
  const visible = needle ? candidates.filter((candidate) => candidate.path.toLowerCase().includes(needle)) : candidates;
  const selectable = visible.filter((candidate) => !blockers.get(candidate.path)).map((candidate) => candidate.path);
  const tickedVisible = selectable.filter((path) => ticked.has(path)).length;
  const copies = candidates.filter((candidate) => candidate.copyOf).length;

  return (
    <Page width="wide">
      <PageHeader
        title="Find lead files on this computer"
        description="Look through a folder for spreadsheets, contact lists and documents, see which ones hold real leads, and import the good ones together."
        breadcrumbs={[{ label: 'Imports', to: '/imports' }, { label: 'Scan a folder' }]}
      />

      <Card className="mb-4">
        <CardBody className="flex gap-3 text-[13px]">
          <FolderSearch className="mt-0.5 size-5 shrink-0 text-slate-400" aria-hidden />
          <div className="min-w-0 flex-1 space-y-3">
            <ul className="list-disc space-y-1 pl-4 text-slate-700">
              <li>
                Your browser looks through a folder you choose, such as Documents. File names and sizes stay on this computer until you press <strong>Check</strong>.
              </li>
              <li>Checking sends each ticked file to Hearth to read it and count its leads. Nothing is kept unless you import it.</li>
              <li>Program and system folders, Office lock files and empty files are skipped.</li>
            </ul>
            {phase === 'walking' ? (
              <div className="flex flex-wrap items-center gap-3 text-slate-600" role="status">
                <Spinner className="size-4" />
                {walkProgress.copies
                  ? `Looking for identical copies among ${pluralize(walkProgress.found, 'file')}…`
                  : `Looking through the folder… ${formatNumber(walkProgress.visited)} files and folders so far, ${pluralize(walkProgress.found, 'possible lead file')}`}
                <Button size="sm" variant="ghost" icon={X} onClick={() => abort.current?.abort()}>
                  Stop here
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button variant={scan ? 'secondary' : 'primary'} icon={FolderOpen} disabled={busy} onClick={() => void chooseFolder()}>
                  {scan ? 'Choose another folder' : 'Choose a folder'}
                </Button>
                {scan && <ScanSummary scan={scan} found={candidates.length} copies={copies} />}
              </div>
            )}
            {!hasPicker && (
              <p className="text-xs text-slate-500">
                Your browser may ask whether to “upload” the files of the folder. That only lets this page list them here: nothing leaves this computer until you press Check.
              </p>
            )}
            <input
              ref={(node) => {
                folderInput.current = node;
                if (node) node.webkitdirectory = true;
              }}
              type="file"
              multiple
              className="hidden"
              data-testid="folder-input"
              onChange={(event) => {
                // Copied first: clearing the input (so the same folder can be picked again) empties its list.
                const files = Array.from(event.target.files ?? []);
                event.target.value = '';
                if (files.length > 0) void scanFolder((walkOptions) => walkFileList(files, walkOptions));
              }}
            />
          </div>
        </CardBody>
      </Card>

      {scan && candidates.length === 0 && phase !== 'walking' && (
        <Card>
          <EmptyState
            icon={FolderSearch}
            title={`No lead files in “${scan.folderName}”`}
            description="Nothing there is a spreadsheet, CSV, contact card, PDF, Word, web page or text file. Try another folder."
          />
        </Card>
      )}

      {candidates.length > 0 && options && (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <Card className="min-w-0">
            <CardHeader
              title={`${pluralize(candidates.length, 'file')} that may hold leads`}
              description={`${formatNumber(importable.length)} ticked${outcomes.size > 0 ? ' · most good leads first' : ''}`}
            />
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2 text-[13px]">
              <Button
                variant={outcomes.size === 0 ? 'primary' : 'secondary'}
                icon={ScanSearch}
                disabled={busy || checkable.length === 0}
                onClick={() => void check()}
              >
                {checkable.length > 0 ? `Check ${pluralize(checkable.length, 'file')} for leads` : 'Every ticked file is checked'}
              </Button>
              {phase === 'checking' && run && (
                <>
                  <Progress done={run.done} total={run.total} label={`Checked ${formatNumber(run.done)} of ${pluralize(run.total, 'file')}`} />
                  <Button size="sm" variant="ghost" icon={X} onClick={() => abort.current?.abort()}>
                    Stop
                  </Button>
                  {paused > 0 && <span className="text-xs text-slate-500">Hearth limits how fast files are checked; carrying on in a moment…</span>}
                </>
              )}
              <Input
                size="sm"
                icon={Search}
                className="w-full sm:ml-auto sm:w-52"
                placeholder="Filter by name or folder"
                aria-label="Filter files"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
              />
            </div>
            <Table containerClassName="max-h-[70vh] rounded-none! border-0!">
              <THead>
                <tr>
                  <TH className="w-10">
                    <Checkbox
                      aria-label="Tick every file shown"
                      checked={selectable.length > 0 && tickedVisible === selectable.length}
                      indeterminate={tickedVisible > 0 && tickedVisible < selectable.length}
                      disabled={busy || selectable.length === 0}
                      onChange={(event) => toggle(selectable, event.target.checked)}
                    />
                  </TH>
                  <TH>File</TH>
                  <TH className="hidden md:table-cell">Type</TH>
                  <TH align="right" className="hidden md:table-cell">
                    Size
                  </TH>
                  <TH className="hidden md:table-cell">Modified</TH>
                  <TH>Leads</TH>
                </tr>
              </THead>
              <TBody>
                {visible.length === 0 && <TableMessageRow colSpan={6}>No file matches “{filter}”.</TableMessageRow>}
                {visible.map((candidate) => {
                  const blocker = blockers.get(candidate.path) ?? null;
                  const folder = candidate.path.slice(0, Math.max(0, candidate.path.length - candidate.name.length - 1));
                  const type = candidate.extension.slice(1).toUpperCase();
                  return (
                    // Most rows start ticked, so the checkbox alone shows it (a tint on nearly every row reads as noise).
                    <TR key={candidate.path}>
                      <TD className="w-10">
                        <Checkbox
                          aria-label={`Include ${candidate.path}`}
                          checked={ticked.has(candidate.path)}
                          disabled={busy || blocker !== null}
                          onChange={(event) => toggle([candidate.path], event.target.checked)}
                        />
                      </TD>
                      <TD className="py-1.5">
                        {/* Bounded and wrapping: a cell grows to fit text that may not wrap, pushing the table wider than the card. */}
                        <div className="max-w-64 min-w-28 break-words sm:min-w-36">
                          <div className="font-medium text-slate-900">{candidate.name}</div>
                          <div className="text-xs text-slate-500">{folder || scan?.folderName}</div>
                          <div className="text-xs text-slate-500 md:hidden">
                            {type} · {formatFileSize(candidate.size)} · {formatDate(candidate.lastModified)}
                          </div>
                          {candidate.copyOf && <div className="text-xs text-amber-700">Same file as {candidate.copyOf}</div>}
                        </div>
                      </TD>
                      <TD className="hidden md:table-cell">{type}</TD>
                      <TD numeric className="hidden whitespace-nowrap md:table-cell">
                        {formatFileSize(candidate.size)}
                      </TD>
                      <TD className="hidden whitespace-nowrap md:table-cell">{formatDate(candidate.lastModified)}</TD>
                      <TD className="py-1.5">
                        <div className="max-w-80 min-w-32 sm:min-w-44">
                          <FileResult outcome={outcomes.get(candidate.path)} blocker={blocker} checking={inFlight.has(candidate.path)} step={steps.get(candidate.path)} />
                        </div>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </Card>

          <div className="space-y-4">
            <ImportOptionCards value={options} onChange={(patch) => setOptions((current) => current && { ...current, ...patch })} source={false} />
            <Card>
              <CardBody className="space-y-2 text-[13px]">
                <Button
                  variant={outcomes.size > 0 ? 'primary' : 'secondary'}
                  iconRight={ArrowRight}
                  className="w-full"
                  disabled={busy || importable.length === 0 || !validCountry(options.defaultCountry)}
                  loading={phase === 'importing'}
                  onClick={() => void startImport()}
                >
                  Import {pluralize(importable.length, 'file')} now
                </Button>
                {phase === 'importing' && run ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Progress done={run.done} total={run.total} label={`${formatNumber(run.done)} of ${pluralize(run.total, 'file')} uploaded`} />
                    <Button size="sm" variant="ghost" icon={X} onClick={() => abort.current?.abort()}>
                      Stop after this file
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">
                    Each file becomes its own import with these settings, and its leads are added straight away. Anything unsure — possible duplicates, rows that
                    don’t look like leads — is left for you to review on the next page.
                  </p>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    </Page>
  );
}

function ScanSummary({ scan, found, copies }: { scan: ScanResult; found: number; copies: number }) {
  const cut =
    scan.limit === 'entries'
      ? `Stopped after ${formatNumber(SCAN_LIMITS.entries)} files and folders — choose a smaller folder (such as a “Clients” or “Leads” folder) to see the rest.`
      : scan.limit === 'candidates'
        ? `Stopped at ${formatNumber(SCAN_LIMITS.candidates)} possible lead files — choose a smaller folder to see the rest.`
        : scan.limit === 'stopped'
          ? 'You stopped the search: files further in are not listed.'
          : null;
  return (
    <div className="text-slate-600">
      In “{scan.folderName}”: {pluralize(found, 'possible lead file')} among {formatNumber(scan.visited)} files and folders
      {scan.skippedFolders > 0 && <> · {pluralize(scan.skippedFolders, 'program or system folder')} skipped</>}
      {copies > 0 && <> · {pluralize(copies, 'identical copy', 'identical copies')} left unticked</>}
      {cut && <p className="mt-1 text-amber-700">{cut}</p>}
    </div>
  );
}

function Progress({ done, total, label }: { done: number; total: number; label: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs text-slate-600">
      <span role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={total} aria-valuenow={done} className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-200">
        <span className="block h-full bg-ember-500 transition-[width]" style={{ width: `${total > 0 ? (done / total) * 100 : 0}%` }} />
      </span>
      {label}
    </span>
  );
}

const STEP_LABELS: Record<Exclude<ImportStep['state'], 'failed' | 'needs-matching'>, string> = {
  uploading: 'Uploading…',
  done: 'Uploaded — importing its leads',
};

/** The "Leads" cell: import progress, then the check's answer, then why the file cannot be imported. */
function FileResult({ outcome, blocker, checking, step }: { outcome: ProbeOutcome | undefined; blocker: string | null; checking: boolean; step: ImportStep | undefined }) {
  if (step?.state === 'failed') return <span className="text-red-700">Not imported: {step.message}</span>;
  if (step?.state === 'needs-matching') return <span className="text-amber-700">Uploaded — needs column matching</span>;
  if (step) {
    return (
      <span className="inline-flex items-center gap-1.5 text-slate-600">
        {step.state !== 'done' && <Spinner className="size-3.5" />}
        {STEP_LABELS[step.state]}
      </span>
    );
  }
  if (checking) {
    return (
      <span className="inline-flex items-center gap-1.5 text-slate-500">
        <Spinner className="size-3.5" /> Checking…
      </span>
    );
  }
  if (outcome?.kind === 'unreadable') return <span className="text-amber-700">Can’t read: {outcome.message}</span>;
  if (blocker) return <span className="text-amber-700">{blocker}</span>;
  if (outcome?.kind === 'failed') return <span className="text-red-700">Not checked: {outcome.message}</span>;
  if (outcome?.kind === 'read') return <ProbeSummary probe={outcome.probe} />;
  return <span className="text-slate-400">Not checked yet</span>;
}

function ProbeSummary({ probe }: { probe: ImportProbeDTO }) {
  if (!probe.hasNameColumn) {
    return (
      <span className="text-amber-700">
        No business-name column — needs column matching after import ({pluralize(probe.totalRows, 'row')})
      </span>
    );
  }
  const { good, weak, junk } = probe.leads;
  const total = good + weak + junk;
  if (total === 0) return <span className="text-slate-500">No leads found</span>;
  return (
    <div className="min-w-0">
      <div>
        <strong className="text-slate-900">{pluralize(total, 'lead')}</strong> · <span className="text-green-700">{formatNumber(good)} good</span> ·{' '}
        {formatNumber(weak)} weak · <span className="text-slate-500">{formatNumber(junk)} junk</span>
      </div>
      {probe.sampleNames.length > 0 && (
        <div className="line-clamp-1 text-xs text-slate-500" title={probe.sampleNames.join(', ')}>
          {probe.sampleNames.join(', ')}
        </div>
      )}
    </div>
  );
}
